import type { PdfEditorState } from '@urbanx/agx.ui-components2';
import { importRepositionableFields } from '@urbanx/agx.ui-components2';
import { PDFDocument } from 'pdf-lib';
import { nswContractExchangeDocuSignAnchors } from './contractExchangeDocuSignAnchors';

export const fitContractExchangeMappingFields = (editState?: PdfEditorState) =>
  editState && {
    ...editState,
    mappingFields: editState.mappingFields?.map((field) => ({
      ...field,
      fitText: field.fitText ?? true,
    })),
  };

// Before authorities were separated, they followed the contract. Keep saved edits on their
// original document when regeneration moves those pages to the front.
export const reorderContractExchangeEdits = async (
  saved: PdfEditorState,
  current: PdfEditorState,
  getSourcePdf: () => Promise<Uint8Array>
): Promise<PdfEditorState> => {
  const contractStart = (state: PdfEditorState) =>
    state.mappingFields?.find((field) => field.mappingName === 'ContractDateAnchor')?.pageIndex ??
    state.mappingFields?.find((field) => field.mappingName === 'Buyer1Name')?.pageIndex ??
    0;
  const previousStart = contractStart(saved);
  const nextStart = contractStart(current);
  if (previousStart === nextStart) return saved;

  let contractPageCount = 0;
  if (previousStart === 0) {
    const sourcePdf = await getSourcePdf();
    const authorityFields = await importRepositionableFields(
      sourcePdf,
      Array.from({ length: 4 }, (_, index) => [
        {
          name: `AuthorityBuyerSignature${index + 1}`,
          anchorText: nswContractExchangeDocuSignAnchors.authorityBuyerSignature(index + 1),
        },
        {
          name: `AuthorityVendorSignature${index + 1}`,
          anchorText: nswContractExchangeDocuSignAnchors.authorityVendorSignature(index + 1),
        },
      ]).flat()
    );
    contractPageCount = Math.min(
      (await PDFDocument.load(sourcePdf)).getPageCount(),
      ...(authorityFields.mappingFields ?? []).map((field) => field.pageIndex)
    );
  }
  const movePage = (pageIndex: number) => {
    if (previousStart === 0) {
      if (pageIndex < contractPageCount) return pageIndex + nextStart;
      const authorityPage = pageIndex - contractPageCount;
      if (authorityPage >= nextStart)
        throw new Error('A page with saved Authority edits no longer exists.');
      return authorityPage;
    }
    if (pageIndex < previousStart) {
      if (pageIndex >= nextStart)
        throw new Error('A page with saved Authority edits no longer exists.');
      return pageIndex;
    }
    return pageIndex - previousStart + nextStart;
  };
  return {
    ...saved,
    textBoxes: saved.textBoxes.map((value) => ({ ...value, pageIndex: movePage(value.pageIndex) })),
    strikeOuts: saved.strikeOuts.map((value) => ({
      ...value,
      pageIndex: movePage(value.pageIndex),
    })),
    fieldBlocks: saved.fieldBlocks.map((value) => ({
      ...value,
      pageIndex: movePage(value.pageIndex),
    })),
    mappingFields: saved.mappingFields?.map((field) => {
      const pageIndex = movePage(field.pageIndex);
      if (!field.source || !('anchorRect' in field.source)) return { ...field, pageIndex };
      const imported = current.mappingFields?.find(
        (candidate) =>
          candidate.mappingName === field.mappingName &&
          candidate.pageIndex === pageIndex &&
          candidate.source?.anchorIndex === field.source?.anchorIndex
      );
      return {
        ...field,
        id: imported?.id ?? field.id,
        pageIndex,
        source: { ...field.source, anchorRect: { ...field.source.anchorRect, pageIndex } },
      };
    }),
  };
};
